<template>
    <b-modal
        :id="'update-explanation-part-model-' + partIn.id"
        :ref="'update-explanation-part-model-' + partIn.id"
        :title="name"
        :ok-title="isExplanationWithFiles() ? $t('common.close') : $t('common.save')"
        :cancel-title="$t('common.cancel')"
        cancel-variant="outline-primary"
        ok-variant="primary"
        :ok-only="isExplanationWithFiles()"
        size="lg"
        @ok="onOk()"
        @show="onShow()"
        header-class="edit-modal-header"
        body-class="edit-modal-body"
        content-class="edit-modal-content"
        modal-class="apprentice-default-modal edit-modal"
        hide-header-close
        :no-enforce-focus="true">
        <div
            class="explanation-text"
            v-if="partIn.type_name === 'explanation_text'">
            <ckeditor
                v-model="text"
                @ready="onReady"
                :editor="editor"
                :config="editorConfig"
                wrap></ckeditor>
        </div>

        <div
            class="explanation-attachment-files"
            v-if="isExplanationWithFiles()">
            <div
                class="upload"
                v-if="!loading && partIn.part_files.length">
                <div
                    class="explanation-attachment-file file"
                    v-for="file of partIn.part_files"
                    :key="file.id">
                    <div
                        class="icon icon-type"
                        :class="getFileType(file.file.extension)"></div>
                    <div class="title">
                        {{ file.file.full_name }}
                        <div class="file-size text-muted">
                            {{ file.file.size }}
                        </div>
                    </div>
                    <div class="remove-file">
                        <b-button
                            class="btn-remove btn-remove-files"
                            variant="danger-icon"
                            @click="removeFile(file)">
                        </b-button>
                    </div>
                </div>
                <div v-if="fileError">
                    <b-alert
                        show
                        variant="danger">
                        {{ $t('assignment.error_file_not_correct_type') }}
                    </b-alert>
                </div>
            </div>

            <div
                class="not-found with-header empty-file-list"
                v-else-if="!loading">
                <div
                    class="btn-add-circle icon"
                    @click="$refs.fileUpload.click()"></div>
                <div v-if="fileError">
                    <b-alert
                        show
                        variant="danger">
                        {{ $t('assignment.error_file_not_correct_type') }}
                    </b-alert>
                </div>

                <div
                    class="alert-notice"
                    v-if="fileAcceptedType === 'image/*'">
                    {{ $t('assignment.no_files_added') }} <br />
                    {{ $t('assignment.accepted_file_types') }}: .png, .jpg, .jpeg, .jif en .jfif
                </div>
                <div
                    class="alert-notice"
                    v-else>
                    {{ $t('assignment.no_files_added') }}
                </div>
            </div>

            <div
                class="loading"
                v-else>
                <i class="fa fa-spinner fa-spin"></i>
            </div>

            <div class="add-new-image">
                <b-button
                    class="btn-upload btn-file-upload"
                    v-if="!loading && partIn.part_files.length >= 1"
                    variant="primary"
                    @click="$refs.fileUpload.click()"
                    :disabled="loading">
                    {{ uploadButtonText }}
                </b-button>

                <input
                    v-show="false"
                    v-on:change="uploadFilesChanged"
                    ref="fileUpload"
                    type="file"
                    :accept="fileAcceptedType"
                    multiple />
            </div>
        </div>

        <div v-if="partIn.type_name === 'explanation_video_link'">
            <label>{{ $t('assignment.youtube_link') }}</label>
            <b-form-input
                v-model="youtubeLink"
                :placeholder="$t('assignment.set_youtube_link')">
            </b-form-input>
        </div>
    </b-modal>
</template>

<script>
import ApprenticeEditor from '../../../../../../../helpers/ApprenticeEditor';

export default {
    name: 'design-2021-lesson-module-assignment-update-explanation-part-modal-component',
    props: ['partIn'],
    data() {
        return {
            name: this.partIn.name
                ? this.partIn.name + ' ' + this.$t('assignment.edit')
                : this.$t('assignment.edit_part'),
            text:
                this.partIn.type_name === 'explanation_text' && this.partIn.text.value !== null
                    ? this.partIn.text.value
                    : '',
            loading: false,
            files: this.partIn.part_files,
            youtubeLink: this.partIn.videoLink != null ? this.partIn.videoLink.value : '',
            editor: ApprenticeEditor,
            editorConfig: {
                contentsCss: 'test',
                bodyClass: 'mybodyclass',
                toolbar: {
                    items: [
                        'undo',
                        'redo',
                        '|',
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        '|',
                        'link',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'outdent',
                        'indent',
                        'alignment',
                        '|',
                        'imageInsert',
                        'mediaEmbed',
                        'insertTable',
                        '|',
                        'fontColor',
                        'fontFamily',
                        'fontSize',
                        'fontBackgroundColor',
                        'highlight',
                        'specialCharacters',
                        '|',
                        'horizontalLine',
                    ],
                },
                language: this.$authUser.locale,
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
                },
                simpleUpload: {
                    uploadUrl: this.partIn.text ? this.routes.assignmentPartTextImageUpload(this.partIn.text.id) : '',
                },
            },
            fileError: false,
        };
    },
    watch: {
        partIn: function (part) {
            this.name = part.name ? part.name + ' ' + this.$t('assignment.edit') : this.$t('assignment.edit_part');

            this.text = part.type_name === 'explanation_text' && part.text.value !== null ? part.text.value : '';
            this.files = part.part_files;
            this.youtubeLink = part.videoLink;
        },
    },
    computed: {
        uploadButtonText() {
            if (this.partIn.type_name === 'explanation_image') {
                return this.$t('assignment.upload_images');
            }
            if (this.partIn.type_name === 'explanation_video') {
                return this.$t('assingment.upload_videos');
            }
            if (this.partIn.type_name === 'explanation_attachment') {
                return this.$t('assignment.upload_files');
            }
            if (this.partIn.type_name === 'explanation_audio') {
                return this.$t('assignment.upload_audio');
            }
            return '';
        },
        fileAcceptedType() {
            if (this.partIn.type_name === 'explanation_image') {
                return 'image/*';
            }
            if (this.partIn.type_name === 'explanation_video') {
                return 'video/*';
            }
            if (this.partIn.type_name === 'explanation_attachment') {
                return '*';
            }
            if (this.partIn.type_name === 'explanation_audio') {
                return 'audio/*';
            }
            return '';
        },
    },
    methods: {
        onOk() {
            if (this.isExplanationWithFiles()) {
                return;
            }

            if (this.partIn.type_name === 'explanation_text') {
                this.saveTextType();
                return;
            }

            if (this.partIn.type_name === 'explanation_video_link') {
                this.saveYoutubeLinkType();
            }
        },
        onShow() {
            this.name = this.partIn.name
                ? this.partIn.name + ' ' + this.$t('assignment.edit')
                : this.$t('assignment.edit_part');

            this.text =
                this.partIn.type_name === 'explanation_text' && this.partIn.text.value !== null
                    ? this.partIn.text.value
                    : '';
            this.files = this.partIn.part_files;
            this.youtubeLink = this.partIn.videoLink;
            this.fileError = false;
            this.loading = false;
        },
        saveTextType() {
            this.alert.saving(this.$t('assignment.saving_part_title'), this.$t('assignment.saving_part'));

            const data = {
                value: this.text,
            };

            this.axios.post(this.routes.assignmentPartTextUpdate(this.partIn.text.id), data).then(
                (response) => {
                    this.$emit('part-updated', response.data);
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        saveYoutubeLinkType() {
            this.alert.saving(this.$t('assignment.saving_part_title'), this.$t('assignment.saving_part'));

            const data = {
                value: this.youtubeLink,
            };

            this.axios.post(this.routes.assignmentPartVideoLinkUpdate(this.partIn.video_link.id), data).then(
                (response) => {
                    this.$emit('part-updated', response.data);
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        removeFile(file) {
            this.alert
                .confirm(this.$t('assignment.delete_file'), this.$t('assignment.delete_file_confirm'))
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.alert.saving();
                    this.axios.delete(this.routes.assignmentPartFileDelete(file.id)).then(
                        (response) => {
                            this.$emit('part-updated', response.data);
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                });
        },
        uploadFilesChanged() {
            this.loading = true;
            const files = this.$refs.fileUpload.files;

            let data = new FormData();
            let count = 0;
            for (const file of files) {
                this.fileError = false;
                this.loading = true;

                data.append('files[' + count + ']', file);
                count++;
            }
            const headers = { 'Content-Type': 'multipart/form-data' };

            this.axios.post(this.routes.assignmentPartFileStore(this.partIn.id), data, { headers: headers }).then(
                (response) => {
                    let part = this.partIn;
                    part.part_files.push(...response.data);
                    this.$emit('part-updated', part);
                    this.loading = false;
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        getFileType(filetype) {
            switch (filetype) {
                case 'jpeg':
                case 'jpg':
                case 'png':
                case 'jif':
                case 'jfif':
                case 'gif':
                    return 'btn-image-type ';

                case 'pdf':
                    return 'btn-file-type ';

                case 'doc':
                case 'docx':
                    return 'btn-doc-type ';

                case 'ppt':
                case 'pptx':
                    return 'btn-doc-type ';

                case 'xls':
                case 'xlx':
                    return 'btn-doc-type ';

                case 'cda':
                case 'mp3':
                case 'oga':
                    return 'btn-audio-type ';

                case 'mov':
                case 'mpeg':
                case 'mkv':
                case 'ogv':
                case 'webm':
                case '3gp':
                    return 'btn-video-type ';

                default:
                    return 'btn-file-type ';
            }
        },

        onReady() {
            const wrapper = document.querySelector('.ck-body-wrapper');
            wrapper.remove();
            const editor = document.querySelector('.ck-editor');
            editor.appendChild(wrapper);
        },

        isExplanationWithFiles() {
            return this.partIn.type === 1 && [2, 3, 4, 6].includes(this.partIn.sub_type);
        },
    },
};
</script>

<style scoped lang="scss">
.loading {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 2rem;
    color: $primary;
    padding-top: map-get($grid-gutter-spacing, 'twenty-one');
    padding-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.explanation-attachment-files {
    flex: 1 0 100%;

    .file {
        display: flex;
        justify-content: flex-start;
        align-content: center;
        font-size: 1.3125rem;

        .icon {
            align-self: center;
            padding: 10px;
            background: $secondary-background-color;
            border-radius: $border-radius-content;
            color: $primary;
        }

        .title {
            font-size: $default-font-size;
            font-family: $headings-font-family;
            font-weight: $font-weight-bold;
            margin-left: map-get($grid-gutter-spacing, 'ten');
            margin-right: map-get($grid-gutter-spacing, 'ten');
            align-self: center;
            word-break: break-word;

            .file-size {
                font-size: 0.75rem;
                font-weight: $font-weight-normal;
            }
        }

        .remove-file {
            display: flex;
            align-items: center;
            align-self: center;
            margin-left: auto;

            .btn-file-download {
                font-size: 1.3125rem;
                &:before {
                    margin-right: 0;
                }
            }
        }

        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }
    }
}

.not-found {
    margin-top: map-get($grid-gutter-spacing, 'forty-two');
}

.add-new-image {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');
}
</style>
